import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _toConsumableArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _classCallCheck from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/createClass.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { isPlainObject, dateTimestampInSeconds, uuid4, logger } from '@sentry/utils';
import { getGlobalEventProcessors, notifyEventProcessors } from './eventProcessors.js';
import { updateSession } from './session.js';
import { applyScopeDataToEvent } from './utils/applyScopeDataToEvent.js';
/**
 * Default value for maximum number of breadcrumbs added to an event.
 */

var DEFAULT_MAX_BREADCRUMBS = 100;
/**
 * The global scope is kept in this module.
 * When accessing this via `getGlobalScope()` we'll make sure to set one if none is currently present.
 */

var globalScope;
/**
 * Holds additional event information. {@link Scope.applyToEvent} will be
 * called by the client before an event will be sent.
 */

var Scope = /*#__PURE__*/function () {
  /** Flag if notifying is happening. */

  /** Callback for client to receive scope changes. */

  /** Callback list that will be called after {@link applyToEvent}. */

  /** Array of breadcrumbs. */

  /** User */

  /** Tags */

  /** Extra */

  /** Contexts */

  /** Attachments */

  /** Propagation Context for distributed tracing */

  /**
   * A place to stash data which is needed at some point in the SDK's event processing pipeline but which shouldn't get
   * sent to Sentry
   */

  /** Fingerprint */

  /** Severity */
  // eslint-disable-next-line deprecation/deprecation

  /**
   * Transaction Name
   */

  /** Span */

  /** Session */

  /** Request Mode Session Status */

  /** The client on this scope */
  // NOTE: Any field which gets added here should get added not only to the constructor but also to the `clone` method.
  function Scope() {
    _classCallCheck(this, Scope);

    this._notifyingListeners = false;
    this._scopeListeners = [];
    this._eventProcessors = [];
    this._breadcrumbs = [];
    this._attachments = [];
    this._user = {};
    this._tags = {};
    this._extra = {};
    this._contexts = {};
    this._sdkProcessingMetadata = {};
    this._propagationContext = generatePropagationContext();
  }
  /**
   * Inherit values from the parent scope.
   * @deprecated Use `scope.clone()` and `new Scope()` instead.
   */


  _createClass(Scope, [{
    key: "clone",
    value:
    /**
     * Clone this scope instance.
     */
    function clone() {
      var newScope = new Scope();
      newScope._breadcrumbs = _toConsumableArray(this._breadcrumbs);
      newScope._tags = _objectSpread({}, this._tags);
      newScope._extra = _objectSpread({}, this._extra);
      newScope._contexts = _objectSpread({}, this._contexts);
      newScope._user = this._user;
      newScope._level = this._level;
      newScope._span = this._span;
      newScope._session = this._session;
      newScope._transactionName = this._transactionName;
      newScope._fingerprint = this._fingerprint;
      newScope._eventProcessors = _toConsumableArray(this._eventProcessors);
      newScope._requestSession = this._requestSession;
      newScope._attachments = _toConsumableArray(this._attachments);
      newScope._sdkProcessingMetadata = _objectSpread({}, this._sdkProcessingMetadata);
      newScope._propagationContext = _objectSpread({}, this._propagationContext);
      newScope._client = this._client;
      return newScope;
    }
    /** Update the client on the scope. */

  }, {
    key: "setClient",
    value: function setClient(client) {
      this._client = client;
    }
    /**
     * Get the client assigned to this scope.
     *
     * It is generally recommended to use the global function `Sentry.getClient()` instead, unless you know what you are doing.
     */

  }, {
    key: "getClient",
    value: function getClient() {
      return this._client;
    }
    /**
     * Add internal on change listener. Used for sub SDKs that need to store the scope.
     * @hidden
     */

  }, {
    key: "addScopeListener",
    value: function addScopeListener(callback) {
      this._scopeListeners.push(callback);
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "addEventProcessor",
    value: function addEventProcessor(callback) {
      this._eventProcessors.push(callback);

      return this;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "setUser",
    value: function setUser(user) {
      // If null is passed we want to unset everything, but still define keys,
      // so that later down in the pipeline any existing values are cleared.
      this._user = user || {
        email: undefined,
        id: undefined,
        ip_address: undefined,
        segment: undefined,
        username: undefined
      };

      if (this._session) {
        updateSession(this._session, {
          user: user
        });
      }

      this._notifyScopeListeners();

      return this;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "getUser",
    value: function getUser() {
      return this._user;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "getRequestSession",
    value: function getRequestSession() {
      return this._requestSession;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "setRequestSession",
    value: function setRequestSession(requestSession) {
      this._requestSession = requestSession;
      return this;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "setTags",
    value: function setTags(tags) {
      this._tags = _objectSpread(_objectSpread({}, this._tags), tags);

      this._notifyScopeListeners();

      return this;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "setTag",
    value: function setTag(key, value) {
      this._tags = _objectSpread(_objectSpread({}, this._tags), {}, _defineProperty({}, key, value));

      this._notifyScopeListeners();

      return this;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "setExtras",
    value: function setExtras(extras) {
      this._extra = _objectSpread(_objectSpread({}, this._extra), extras);

      this._notifyScopeListeners();

      return this;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "setExtra",
    value: function setExtra(key, extra) {
      this._extra = _objectSpread(_objectSpread({}, this._extra), {}, _defineProperty({}, key, extra));

      this._notifyScopeListeners();

      return this;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "setFingerprint",
    value: function setFingerprint(fingerprint) {
      this._fingerprint = fingerprint;

      this._notifyScopeListeners();

      return this;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "setLevel",
    value: function setLevel( // eslint-disable-next-line deprecation/deprecation
    level) {
      this._level = level;

      this._notifyScopeListeners();

      return this;
    }
    /**
     * Sets the transaction name on the scope for future events.
     */

  }, {
    key: "setTransactionName",
    value: function setTransactionName(name) {
      this._transactionName = name;

      this._notifyScopeListeners();

      return this;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "setContext",
    value: function setContext(key, context) {
      if (context === null) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete this._contexts[key];
      } else {
        this._contexts[key] = context;
      }

      this._notifyScopeListeners();

      return this;
    }
    /**
     * Sets the Span on the scope.
     * @param span Span
     * @deprecated Instead of setting a span on a scope, use `startSpan()`/`startSpanManual()` instead.
     */

  }, {
    key: "setSpan",
    value: function setSpan(span) {
      this._span = span;

      this._notifyScopeListeners();

      return this;
    }
    /**
     * Returns the `Span` if there is one.
     * @deprecated Use `getActiveSpan()` instead.
     */

  }, {
    key: "getSpan",
    value: function getSpan() {
      return this._span;
    }
    /**
     * Returns the `Transaction` attached to the scope (if there is one).
     * @deprecated You should not rely on the transaction, but just use `startSpan()` APIs instead.
     */

  }, {
    key: "getTransaction",
    value: function getTransaction() {
      // Often, this span (if it exists at all) will be a transaction, but it's not guaranteed to be. Regardless, it will
      // have a pointer to the currently-active transaction.
      var span = this._span; // Cannot replace with getRootSpan because getRootSpan returns a span, not a transaction
      // Also, this method will be removed anyway.
      // eslint-disable-next-line deprecation/deprecation

      return span && span.transaction;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "setSession",
    value: function setSession(session) {
      if (!session) {
        delete this._session;
      } else {
        this._session = session;
      }

      this._notifyScopeListeners();

      return this;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "getSession",
    value: function getSession() {
      return this._session;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "update",
    value: function update(captureContext) {
      if (!captureContext) {
        return this;
      }

      var scopeToMerge = typeof captureContext === 'function' ? captureContext(this) : captureContext;

      if (scopeToMerge instanceof Scope) {
        var scopeData = scopeToMerge.getScopeData();
        this._tags = _objectSpread(_objectSpread({}, this._tags), scopeData.tags);
        this._extra = _objectSpread(_objectSpread({}, this._extra), scopeData.extra);
        this._contexts = _objectSpread(_objectSpread({}, this._contexts), scopeData.contexts);

        if (scopeData.user && Object.keys(scopeData.user).length) {
          this._user = scopeData.user;
        }

        if (scopeData.level) {
          this._level = scopeData.level;
        }

        if (scopeData.fingerprint.length) {
          this._fingerprint = scopeData.fingerprint;
        }

        if (scopeToMerge.getRequestSession()) {
          this._requestSession = scopeToMerge.getRequestSession();
        }

        if (scopeData.propagationContext) {
          this._propagationContext = scopeData.propagationContext;
        }
      } else if (isPlainObject(scopeToMerge)) {
        var scopeContext = captureContext;
        this._tags = _objectSpread(_objectSpread({}, this._tags), scopeContext.tags);
        this._extra = _objectSpread(_objectSpread({}, this._extra), scopeContext.extra);
        this._contexts = _objectSpread(_objectSpread({}, this._contexts), scopeContext.contexts);

        if (scopeContext.user) {
          this._user = scopeContext.user;
        }

        if (scopeContext.level) {
          this._level = scopeContext.level;
        }

        if (scopeContext.fingerprint) {
          this._fingerprint = scopeContext.fingerprint;
        }

        if (scopeContext.requestSession) {
          this._requestSession = scopeContext.requestSession;
        }

        if (scopeContext.propagationContext) {
          this._propagationContext = scopeContext.propagationContext;
        }
      }

      return this;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "clear",
    value: function clear() {
      this._breadcrumbs = [];
      this._tags = {};
      this._extra = {};
      this._user = {};
      this._contexts = {};
      this._level = undefined;
      this._transactionName = undefined;
      this._fingerprint = undefined;
      this._requestSession = undefined;
      this._span = undefined;
      this._session = undefined;

      this._notifyScopeListeners();

      this._attachments = [];
      this._propagationContext = generatePropagationContext();
      return this;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "addBreadcrumb",
    value: function addBreadcrumb(breadcrumb, maxBreadcrumbs) {
      var maxCrumbs = typeof maxBreadcrumbs === 'number' ? maxBreadcrumbs : DEFAULT_MAX_BREADCRUMBS; // No data has been changed, so don't notify scope listeners

      if (maxCrumbs <= 0) {
        return this;
      }

      var mergedBreadcrumb = _objectSpread({
        timestamp: dateTimestampInSeconds()
      }, breadcrumb);

      var breadcrumbs = this._breadcrumbs;
      breadcrumbs.push(mergedBreadcrumb);
      this._breadcrumbs = breadcrumbs.length > maxCrumbs ? breadcrumbs.slice(-maxCrumbs) : breadcrumbs;

      this._notifyScopeListeners();

      return this;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "getLastBreadcrumb",
    value: function getLastBreadcrumb() {
      return this._breadcrumbs[this._breadcrumbs.length - 1];
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "clearBreadcrumbs",
    value: function clearBreadcrumbs() {
      this._breadcrumbs = [];

      this._notifyScopeListeners();

      return this;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "addAttachment",
    value: function addAttachment(attachment) {
      this._attachments.push(attachment);

      return this;
    }
    /**
     * @inheritDoc
     * @deprecated Use `getScopeData()` instead.
     */

  }, {
    key: "getAttachments",
    value: function getAttachments() {
      var data = this.getScopeData();
      return data.attachments;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "clearAttachments",
    value: function clearAttachments() {
      this._attachments = [];
      return this;
    }
    /** @inheritDoc */

  }, {
    key: "getScopeData",
    value: function getScopeData() {
      var _breadcrumbs = this._breadcrumbs,
          _attachments = this._attachments,
          _contexts = this._contexts,
          _tags = this._tags,
          _extra = this._extra,
          _user = this._user,
          _level = this._level,
          _fingerprint = this._fingerprint,
          _eventProcessors = this._eventProcessors,
          _propagationContext = this._propagationContext,
          _sdkProcessingMetadata = this._sdkProcessingMetadata,
          _transactionName = this._transactionName,
          _span = this._span;
      return {
        breadcrumbs: _breadcrumbs,
        attachments: _attachments,
        contexts: _contexts,
        tags: _tags,
        extra: _extra,
        user: _user,
        level: _level,
        fingerprint: _fingerprint || [],
        eventProcessors: _eventProcessors,
        propagationContext: _propagationContext,
        sdkProcessingMetadata: _sdkProcessingMetadata,
        transactionName: _transactionName,
        span: _span
      };
    }
    /**
     * Applies data from the scope to the event and runs all event processors on it.
     *
     * @param event Event
     * @param hint Object containing additional information about the original exception, for use by the event processors.
     * @hidden
     * @deprecated Use `applyScopeDataToEvent()` directly
     */

  }, {
    key: "applyToEvent",
    value: function applyToEvent(event) {
      var hint = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      var additionalEventProcessors = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];
      applyScopeDataToEvent(event, this.getScopeData()); // TODO (v8): Update this order to be: Global > Client > Scope

      var eventProcessors = [].concat(_toConsumableArray(additionalEventProcessors), _toConsumableArray(getGlobalEventProcessors()), _toConsumableArray(this._eventProcessors));
      return notifyEventProcessors(eventProcessors, event, hint);
    }
    /**
     * Add data which will be accessible during event processing but won't get sent to Sentry
     */

  }, {
    key: "setSDKProcessingMetadata",
    value: function setSDKProcessingMetadata(newData) {
      this._sdkProcessingMetadata = _objectSpread(_objectSpread({}, this._sdkProcessingMetadata), newData);
      return this;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "setPropagationContext",
    value: function setPropagationContext(context) {
      this._propagationContext = context;
      return this;
    }
    /**
     * @inheritDoc
     */

  }, {
    key: "getPropagationContext",
    value: function getPropagationContext() {
      return this._propagationContext;
    }
    /**
     * Capture an exception for this scope.
     *
     * @param exception The exception to capture.
     * @param hint Optinal additional data to attach to the Sentry event.
     * @returns the id of the captured Sentry event.
     */

  }, {
    key: "captureException",
    value: function captureException(exception, hint) {
      var eventId = hint && hint.event_id ? hint.event_id : uuid4();

      if (!this._client) {
        logger.warn('No client configured on scope - will not capture exception!');
        return eventId;
      }

      var syntheticException = new Error('Sentry syntheticException');

      this._client.captureException(exception, _objectSpread(_objectSpread({
        originalException: exception,
        syntheticException: syntheticException
      }, hint), {}, {
        event_id: eventId
      }), this);

      return eventId;
    }
    /**
     * Capture a message for this scope.
     *
     * @param message The message to capture.
     * @param level An optional severity level to report the message with.
     * @param hint Optional additional data to attach to the Sentry event.
     * @returns the id of the captured message.
     */

  }, {
    key: "captureMessage",
    value: function captureMessage(message, level, hint) {
      var eventId = hint && hint.event_id ? hint.event_id : uuid4();

      if (!this._client) {
        logger.warn('No client configured on scope - will not capture message!');
        return eventId;
      }

      var syntheticException = new Error(message);

      this._client.captureMessage(message, level, _objectSpread(_objectSpread({
        originalException: message,
        syntheticException: syntheticException
      }, hint), {}, {
        event_id: eventId
      }), this);

      return eventId;
    }
    /**
     * Captures a manually created event for this scope and sends it to Sentry.
     *
     * @param exception The event to capture.
     * @param hint Optional additional data to attach to the Sentry event.
     * @returns the id of the captured event.
     */

  }, {
    key: "captureEvent",
    value: function captureEvent(event, hint) {
      var eventId = hint && hint.event_id ? hint.event_id : uuid4();

      if (!this._client) {
        logger.warn('No client configured on scope - will not capture event!');
        return eventId;
      }

      this._client.captureEvent(event, _objectSpread(_objectSpread({}, hint), {}, {
        event_id: eventId
      }), this);

      return eventId;
    }
    /**
     * This will be called on every set call.
     */

  }, {
    key: "_notifyScopeListeners",
    value: function _notifyScopeListeners() {
      var _this = this;

      // We need this check for this._notifyingListeners to be able to work on scope during updates
      // If this check is not here we'll produce endless recursion when something is done with the scope
      // during the callback.
      if (!this._notifyingListeners) {
        this._notifyingListeners = true;

        this._scopeListeners.forEach(function (callback) {
          callback(_this);
        });

        this._notifyingListeners = false;
      }
    }
  }], [{
    key: "clone",
    value: function clone(scope) {
      return scope ? scope.clone() : new Scope();
    }
  }]);

  return Scope;
}();
/**
 * Get the global scope.
 * This scope is applied to _all_ events.
 */


function getGlobalScope() {
  if (!globalScope) {
    globalScope = new Scope();
  }

  return globalScope;
}
/**
 * This is mainly needed for tests.
 * DO NOT USE this, as this is an internal API and subject to change.
 * @hidden
 */


function setGlobalScope(scope) {
  globalScope = scope;
}

function generatePropagationContext() {
  return {
    traceId: uuid4(),
    spanId: uuid4().substring(16)
  };
}

export { Scope, getGlobalScope, setGlobalScope };